// Detalle de un cliente (Store) dentro de Liquidaciones > Clientes: lista
// de envíos de la semana WYN actual con su estado OPERATIVO real (nunca
// "Valorizado" — eso es un estado del proceso financiero de Lynx, no del
// envío) y edición financiera manual: Turbo, ×2 Doble y Precio manual. El
// override nunca destruye el cálculo automático: la UI conserva CALCULADO
// y FINAL como conceptos separados. Mismo patrón de overlay que
// EnvioLegacyDetailModal.tsx.
import React, { useEffect, useMemo, useState } from "react";
import Card from "../ui/Card";
import Button from "../ui/Button";
import Input from "../ui/Input";
import { authFetch } from "../../lib/authFetch";
import { downloadAuthenticatedFile } from "../../lib/downloadFile";
import OverrideEditModal, { DeliveryDetail } from "./OverrideEditModal";

type ClienteSummary = {
  deliveries: number;
  pricedDeliveries: number;
  unresolvedDeliveries: number;
  subtotalBeforeDiscount: number;
  discountAmount: number;
  discount: number;
  total: number; // "Total liquidación": subtotal - descuento, SIN cobranzas
  // Cobranzas (ver informe "Nueva respuesta LightData"): dinero del Seller
  // cobrado en la entrega — SIEMPRE resta de la liquidación. Si supera el
  // total, el neto pasa a mostrarse como "Monto a rendir" (nunca negativo).
  cobranzaTotal: number;
  settleType: "TOTAL_A_PAGAR" | "MONTO_A_RENDIR";
  netAmount: number;
};

type ClienteDetailResponse = {
  ok: boolean;
  // readOnly (ver informe "Sprint Final — Cierre semanal", Parte 4): true
  // en una semana ya CERRADA — misma pantalla, mismos datos, pero sin
  // ningún botón de edición. El backend es la autoridad real (bloquea el
  // override igual aunque alguien fuerce el request); esto es solo para no
  // mostrar un botón que el server va a rechazar.
  period: { fromDisplay: string; toDisplay: string; readOnly?: boolean };
  store: { id: number; name: string | null };
  summary: ClienteSummary;
  deliveries: DeliveryDetail[];
};

type Props = {
  open: boolean;
  storeId: number | string | null;
  storeName: string | null;
  token: string | null;
  onClose: () => void;
  // ausente = semana abierta actual; con valor, abre esa semana histórica
  // (ver informe "Sprint Final — Cierre semanal", Parte 3/4).
  periodKey?: string | null;
  // Se llama después de guardar/quitar un override, para que la pantalla
  // que lista todos los clientes también refresque su total — sin recargar
  // toda la aplicación, solo un re-fetch del resumen.
  onOverrideChanged?: () => void;
};

const overlayStyle: React.CSSProperties = { position: "fixed", inset: 0, zIndex: 120, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" };
const backdropStyle: React.CSSProperties = { position: "absolute", inset: 0, background: "rgba(0,0,0,0.65)" };
const shellStyle: React.CSSProperties = { position: "relative", width: "100%", maxWidth: "1200px", maxHeight: "90vh", overflow: "hidden" };
const headerStyle: React.CSSProperties = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", padding: "18px 20px", borderBottom: "1px solid var(--border)" };
const titleStyle: React.CSSProperties = { margin: 0, fontSize: "18px", fontWeight: 700, color: "var(--text-primary)" };
const subtitleStyle: React.CSSProperties = { margin: "4px 0 0 0", fontSize: "13px", color: "var(--text-secondary)" };
const bodyStyle: React.CSSProperties = { padding: "20px", maxHeight: "76vh", overflowY: "auto", display: "flex", flexDirection: "column", gap: "14px" };
const stateStyle: React.CSSProperties = { padding: "24px 0", color: "var(--text-secondary)", fontSize: "14px", textAlign: "center" };
const tableWrapStyle: React.CSSProperties = { overflowX: "auto", border: "1px solid var(--border)", borderRadius: "14px" };
const tableStyle: React.CSSProperties = { width: "100%", borderCollapse: "collapse" };
const thStyle: React.CSSProperties = { textAlign: "left", padding: "10px", color: "var(--text-secondary)", fontSize: "12px", fontWeight: 700, borderBottom: "1px solid var(--border)", whiteSpace: "nowrap", background: "rgba(255,255,255,0.02)" };
const tdStyle: React.CSSProperties = { padding: "8px 10px", color: "var(--text-primary)", fontSize: "13px", borderBottom: "1px solid var(--border)", whiteSpace: "nowrap" };
const badgeStyle = (color: string): React.CSSProperties => ({ display: "inline-block", padding: "2px 9px", borderRadius: "999px", fontSize: "11px", fontWeight: 700, border: `1px solid ${color}`, color, marginRight: "4px" });
const editButtonStyle: React.CSSProperties = { background: "transparent", border: "1px solid var(--border)", borderRadius: "8px", width: "28px", height: "28px", cursor: "pointer", fontSize: "13px", color: "var(--text-secondary)" };
const searchFieldStyle: React.CSSProperties = { maxWidth: "360px" };
const summaryCardStyle: React.CSSProperties = { border: "1px solid var(--border)", borderRadius: "14px", padding: "14px 18px", display: "flex", flexDirection: "column", gap: "6px", maxWidth: "360px" };
const summaryRowStyle: React.CSSProperties = { display: "flex", justifyContent: "space-between", fontSize: "13px", color: "var(--text-secondary)" };
const summaryDividerStyle: React.CSSProperties = { borderTop: "1px solid var(--border)", margin: "4px 0" };
const summaryTotalRowStyle: React.CSSProperties = { display: "flex", justifyContent: "space-between", fontSize: "16px", fontWeight: 700, color: "var(--text-primary)" };

function money(n: number | null): string {
  if (n === null || n === undefined) return "—";
  return `$${n.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Badges de excepción: ausencia de badge = cálculo normal, nunca se rotula "NORMAL".
function ExceptionBadges({ d }: { d: DeliveryDetail }) {
  return (
    <>
      {d.is_turbo && <span style={badgeStyle(d.turbo_source === "MANUAL" ? "#c99a1f" : "#1f7ac9")}>⚡ Turbo</span>}
      {d.is_double && <span style={badgeStyle("#1f9c7a")}>×2 Doble</span>}
      {d.manual_price !== null && <span style={badgeStyle("#8a3fd1")}>✏️ Manual</span>}
      {d.has_cobranza_override && <span style={badgeStyle("#2f8f5b")}>💰 Cobranza editada</span>}
    </>
  );
}

// Subtotal / Descuento (si existe) / Total liquidación / Cobranzas (si
// existen) / TOTAL A PAGAR o MONTO A RENDIR — ver informe "Nueva respuesta
// LightData". Nunca se muestra un monto negativo: cuando las cobranzas
// superan la liquidación, la etiqueta cambia a "Monto a rendir".
function SummaryBlock({ summary }: { summary: ClienteSummary }) {
  const isRendir = summary.settleType === "MONTO_A_RENDIR";
  return (
    <div style={summaryCardStyle}>
      {summary.discount > 0 && (
        <>
          <div style={summaryRowStyle}><span>Subtotal</span><span>{money(summary.subtotalBeforeDiscount)}</span></div>
          <div style={summaryRowStyle}><span>Descuento</span><span>-{money(summary.discountAmount)}</span></div>
        </>
      )}
      <div style={summaryRowStyle}><span>Total liquidación</span><span>{money(summary.total)}</span></div>
      {summary.cobranzaTotal > 0 && (
        <div style={summaryRowStyle}><span>Cobranzas</span><span>-{money(summary.cobranzaTotal)}</span></div>
      )}
      <div style={summaryDividerStyle} />
      <div style={summaryTotalRowStyle}>
        <span>{isRendir ? "Monto a rendir" : "Total a pagar"}</span>
        <span>{money(summary.netAmount)}</span>
      </div>
    </div>
  );
}

export default function ClienteDetailModal({ open, storeId, storeName, token, onClose, periodKey = null, onOverrideChanged }: Props) {
  const [data, setData] = useState<ClienteDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [editing, setEditing] = useState<DeliveryDetail | null>(null);
  const [search, setSearch] = useState("");
  const periodSuffix = periodKey ? `?period=${encodeURIComponent(periodKey)}` : "";
  const readOnly = data?.period?.readOnly === true;

  useEffect(() => {
    if (!open || storeId === null || !token) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    setSearch("");

    authFetch(`/v1/liquidaciones/clientes/${storeId}${periodSuffix}`, { token })
      .then(async (res) => {
        const payload = await res.json().catch(() => ({}));
        if (!res.ok || payload?.ok === false) throw new Error(payload?.error || "Error al cargar el detalle del cliente");
        if (!cancelled) setData(payload);
      })
      .catch((e: any) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, storeId, token, periodSuffix]);

  // Buscador dentro del detalle: tracking, dirección y destinatario,
  // client-side (los envíos del cliente ya están todos cargados en memoria).
  const filteredDeliveries = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.deliveries;
    return data.deliveries.filter(
      (d) =>
        (d.tracking || "").toLowerCase().includes(q) ||
        (d.direccion || "").toLowerCase().includes(q) ||
        (d.destinatario || "").toLowerCase().includes(q)
    );
  }, [data, search]);

  if (!open) return null;

  const handleDownload = async () => {
    if (storeId === null) return;
    setDownloading(true);
    try {
      await downloadAuthenticatedFile(
        `/v1/liquidaciones/clientes/${storeId}/export${periodSuffix}`,
        token,
        `liquidacion_${storeName || "cliente"}.xlsx`
      );
    } catch (e: any) {
      alert(e.message || "No se pudo descargar el Excel del cliente.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div style={overlayStyle}>
      <div style={backdropStyle} onClick={onClose} />
      <div style={shellStyle}>
        <Card padding="0" style={{ display: "flex", flexDirection: "column" }}>
          <div style={headerStyle}>
            <div>
              <h2 style={titleStyle}>{data?.store?.name || storeName || "Cliente"}</h2>
              {data && (
                <p style={subtitleStyle}>
                  {data.period.fromDisplay} → {data.period.toDisplay} · {data.deliveries.length} envío(s) ·{" "}
                  {data.summary.settleType === "MONTO_A_RENDIR" ? "Monto a rendir" : "Total a pagar"} {money(data.summary.netAmount)}
                  {readOnly && <span style={badgeStyle("#c99a1f")}> Semana cerrada · Solo lectura</span>}
                </p>
              )}
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <Button variant="secondary" onClick={handleDownload} disabled={downloading || !data}>
                {downloading ? "Generando…" : "Descargar Excel"}
              </Button>
              <Button variant="secondary" onClick={onClose}>Cerrar</Button>
            </div>
          </div>

          <div style={bodyStyle}>
            {loading && <div style={stateStyle}>Cargando…</div>}
            {!loading && error && <div style={{ ...stateStyle, color: "var(--danger, #d1242f)" }}>{error}</div>}
            {!loading && !error && data && data.deliveries.length === 0 && (
              <div style={stateStyle}>
                {periodKey ? "Sin envíos para este cliente en esta semana." : "Sin envíos para este cliente en la semana actual."}
              </div>
            )}
            {!loading && !error && data && data.deliveries.length > 0 && (
              <>
                <SummaryBlock summary={data.summary} />
                <div style={searchFieldStyle}>
                  <Input
                    placeholder="Buscar por tracking, dirección o destinatario..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                {filteredDeliveries.length === 0 ? (
                  <div style={stateStyle}>Ningún envío coincide con la búsqueda.</div>
                ) : (
                  <div style={tableWrapStyle}>
                    <table style={tableStyle}>
                      <thead>
                        <tr>
                          <th style={thStyle}>Tracking</th>
                          <th style={thStyle}>Destinatario</th>
                          <th style={thStyle}>CP</th>
                          <th style={thStyle}>Estado</th>
                          <th style={thStyle}>Tarifa</th>
                          <th style={thStyle}>Calculado</th>
                          <th style={thStyle}>Final</th>
                          <th style={thStyle}>Cobranza</th>
                          <th style={thStyle}></th>
                          {!readOnly && <th style={thStyle}></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredDeliveries.map((d) => {
                          const pending = d.final_price === null;
                          return (
                            <tr key={String(d.lightdata_id)} title={pending ? d.pricing_reason || undefined : d.override_observation || undefined}>
                              <td style={tdStyle}>{d.tracking || "—"}</td>
                              <td style={tdStyle}>{d.destinatario || "Sin informar"}</td>
                              <td style={tdStyle}>{d.cp || "—"}</td>
                              <td style={tdStyle}>{d.estado || "—"}</td>
                              <td style={tdStyle}>{d.tariff_name || "—"}</td>
                              <td style={tdStyle}>{money(d.calculated_price)}</td>
                              <td style={tdStyle}>
                                {pending ? <span style={{ color: "var(--danger, #d1242f)" }}>Pendiente</span> : money(d.final_price)}
                              </td>
                              <td style={tdStyle}>{money(d.cobranza ?? null)}</td>
                              <td style={tdStyle}>
                                <ExceptionBadges d={d} />
                              </td>
                              {!readOnly && (
                                <td style={tdStyle}>
                                  <button type="button" style={editButtonStyle} title="Editar precio / Turbo / Doble" onClick={() => setEditing(d)}>
                                    ✎
                                  </button>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </Card>
      </div>

      <OverrideEditModal
        open={editing !== null}
        entityLabel={`Store: ${data?.store?.name || storeName || "—"}`}
        token={token}
        delivery={editing}
        overrideUrl={`/v1/liquidaciones/clientes/${storeId}/envios/${editing?.lightdata_id}/override`}
        onClose={() => setEditing(null)}
        onSaved={(detail) => {
          setData(detail);
          setEditing(null);
          onOverrideChanged?.();
        }}
      />
    </div>
  );
}
